// src/modals/MallPriceListDialog.jsx
import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokenService } from "src/services";
import Services from "src/services/Services";

const VEHICLE_LABELS = {
  "pet-car": "Evcil Hayvan Aracı",
  "kid-car": "Oyuncaklı Çocuk Aracı",
  "gift": "Hediye Paketi",
};

const mapCurrencyFromAPI = (c) => (c === "TRY" ? "TL" : (c || "TL"));

// Edit dialog’daki mantığın sadeleştirilmiş versiyonu:
// vehicleInventoryWithPrice → tablo satırlarına map
// Edit dialog’daki mantığın sadeleştirilmiş versiyonu:
// vehicleInventoryWithPrice → tablo satırlarına map
const toRowsFromInventoryPrice = (inventoryPrice) => {
  if (!inventoryPrice || typeof inventoryPrice !== "object") return [];

  const rows = [];

  Object.entries(inventoryPrice).forEach(([vehicleKey, val]) => {
    if (!val || typeof val !== "object") return;

    const vehicleType = VEHICLE_LABELS[vehicleKey] ?? vehicleKey;

    // --- Yeni şema #1: daily + hourly[] + status ---
    if (Array.isArray(val.hourly) || val.daily) {
      if (Array.isArray(val.hourly)) {
        val.hourly.forEach((h) => {
          if (!h) return;
          rows.push({
            vehicleType,
            duration: String(h.hour ?? h.duration ?? ""),
            price: h.price ?? 0,
            currency: mapCurrencyFromAPI(h.currency),
          });
        });
      }

      if (val.daily) {
        rows.push({
          vehicleType,
          duration: "1 Gün",
          price: val.daily.price ?? 0,
          currency: mapCurrencyFromAPI(val.daily.currency),
        });
      }

      return;
    }

    // --- Gift yeni/eski şema: { price, currency, status } veya { price, currency } ---
    if (vehicleKey === "gift") {
      if (typeof val.price === "undefined") return;

      rows.push({
        vehicleType,
        duration: "-",
        price: val.price ?? 0,
        currency: mapCurrencyFromAPI(val.currency),
      });
      return;
    }

    // --- Eski "prices" objesi şeması ---
    if (val.prices && typeof val.prices === "object") {
      Object.entries(val.prices).forEach(([dur, p]) => {
        rows.push({
          vehicleType,
          duration: dur,
          price: p?.price ?? 0,
          currency: mapCurrencyFromAPI(p?.currency),
        });
      });
      return;
    }

    // --- En eski şema – hour / daily alanları ---
    if (val.hour) {
      rows.push({
        vehicleType,
        duration: "1",
        price: val.hour.price ?? 0,
        currency: mapCurrencyFromAPI(val.hour.currency),
      });
    }
    if (val.daily) {
      rows.push({
        vehicleType,
        duration: "1 Gün",
        price: val.daily.price ?? 0,
        currency: mapCurrencyFromAPI(val.daily.currency),
      });
    }
  });

  return rows;
};

export default function MallPriceListDialog({
  open,
  onClose,
  title = "AVM / PLAZA FİYAT LİSTESİ",
  mallName,
  mallID,
}) {
  const user = tokenService.getUser();

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const token = user?.token || localStorage.getItem("token");
    if (!open || !mallID || !token) return;

    (async () => {
      try {
        setLoading(true);
        const res = await Services.GetMall(token, mallID);
        const result = res?.data?.result;
        const invPrice = result?.vehicleInventoryWithPrice;
        console.log(invPrice)

        const mapped = toRowsFromInventoryPrice(invPrice);
        setRows(mapped);
      } catch (err) {
        console.error("[MallPriceListDialog] GetMall -> error", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, mallID, user?.token]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="mall-price-title"
    >
      <DialogTitle
        id="mall-price-title"
        sx={{ textAlign: "center", fontWeight: 700, pr: 7 }}
      >
        {title}
        <IconButton
          aria-label="Kapat"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: "text.secondary", px: 1.5, pb: 1.5, fontWeight: 600 }}
        >
          {mallName || "-"}
        </Typography>

        <Divider />

        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1.5 }}>
          {loading ? (
            <Table size="medium">
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <Table size="medium" aria-label="Fiyat Listesi">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary" } }}>
                  <TableCell>ARAÇ TİPİ</TableCell>
                  <TableCell>SAAT</TableCell>
                  <TableCell>FİYAT BİLGİSİ</TableCell>
                  <TableCell>PARA BİRİMİ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: "text.secondary" }}>
                      Bu AVM için tanımlı fiyat listesi bulunamadı.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r, i) => (
                    <TableRow key={`${r.vehicleType}-${r.duration}-${i}`}>
                      <TableCell>{r.vehicleType}</TableCell>
                      <TableCell>{r.duration}</TableCell>
                      <TableCell>{r.price}</TableCell>
                      <TableCell>{r.currency}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}